import { useState } from "react";
import { Copy, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { PatRowDTO } from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useApiTokens,
  useCreateApiToken,
  useRevokeApiToken,
  type TokenScope,
} from "./use-api-tokens";

// MCP endpoint lives at the top level of the API worker (not under /api). In
// production that is api.colab.bookboost.io; derive it from VITE_API_URL when
// that's an absolute origin, else fall back to the production host.
function mcpEndpoint(): string {
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (apiUrl && /^https?:\/\//.test(apiUrl)) {
    try {
      return new URL("/mcp", apiUrl).toString();
    } catch {
      /* fall through */
    }
  }
  return "https://api.colab.bookboost.io/mcp";
}

function copy(text: string, label: string) {
  void navigator.clipboard
    .writeText(text)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error("Copy failed"));
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

function isExpired(row: PatRowDTO): boolean {
  return row.expiresAt != null && Date.parse(row.expiresAt) <= Date.now();
}

function statusOf(row: PatRowDTO): string {
  if (row.revokedAt) return "Revoked";
  if (isExpired(row)) return "Expired";
  return "Active";
}

export function ConnectionsPanel({ scope }: { scope: TokenScope }) {
  // The token list is fetched lazily (only once shown) so the settings page
  // makes no API call on initial render.
  const [showTokens, setShowTokens] = useState(false);
  const query = useApiTokens(scope, showTokens);
  const create = useCreateApiToken(scope);
  const revoke = useRevokeApiToken(scope);
  const [name, setName] = useState("");
  const [newToken, setNewToken] = useState<string | null>(null);

  const snippet = `{
  "mcpServers": {
    "colab": {
      "type": "http",
      "url": "${mcpEndpoint()}",
      "headers": { "Authorization": "Bearer YOUR_TOKEN" }
    }
  }
}`;

  function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate(
      { name: trimmed },
      {
        onSuccess: (res) => {
          setNewToken(res.token);
          setName("");
          setShowTokens(true);
        },
      },
    );
  }

  const tokens = query.data ?? [];

  return (
    <section>
      <div className="mb-4">
        <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
          <KeyRound className="h-4 w-4" />
          API access &amp; MCP
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Create a personal access token to connect an AI client (e.g. Claude
          Code) to your Colab data over the Model Context Protocol. A token acts
          on your behalf with your permissions until you revoke it.
        </p>
      </div>

      {/* Create */}
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <div className="grow">
          <Label htmlFor="token-name" className="text-xs">
            Token name
          </Label>
          <Input
            id="token-name"
            value={name}
            placeholder="e.g. My laptop — Claude Code"
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
            }}
          />
        </div>
        <Button onClick={handleCreate} disabled={create.isPending || !name.trim()}>
          {create.isPending ? "Creating…" : "Create token"}
        </Button>
      </div>

      {/* One-time reveal */}
      {newToken && (
        <div className="mb-4 rounded-lg border border-primary/40 bg-primary/5 p-3">
          <p className="text-xs font-medium text-foreground">
            Copy your token now — it won&apos;t be shown again.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="grow overflow-x-auto rounded bg-background px-2 py-1.5 font-mono text-xs">
              {newToken}
            </code>
            <Button size="sm" variant="outline" onClick={() => copy(newToken, "Token")}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
          <button
            type="button"
            className="mt-2 text-xs text-muted-foreground hover:underline"
            onClick={() => setNewToken(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* List (lazy — fetched only when revealed) */}
      {!showTokens ? (
        <Button variant="outline" size="sm" onClick={() => setShowTokens(true)}>
          Show my tokens
        </Button>
      ) : query.isLoading ? (
        <div className="grid gap-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
          Couldn&apos;t load tokens.
        </p>
      ) : tokens.length === 0 ? (
        <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
          No tokens yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Name</th>
                <th className="px-4 py-2 text-left font-medium">Prefix</th>
                <th className="px-4 py-2 text-left font-medium">Created</th>
                <th className="px-4 py-2 text-left font-medium">Last used</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {tokens.map((row, idx) => {
                const inactive = !!row.revokedAt || isExpired(row);
                return (
                  <tr key={row.id} className={idx === 0 ? "" : "border-t"}>
                    <td className="px-4 py-2.5 text-foreground">{row.name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
                      {row.tokenPrefix}…
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {formatDate(row.createdAt)}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {formatDate(row.lastUsedAt)}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{statusOf(row)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {!inactive && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={revoke.isPending}
                          onClick={() => revoke.mutate(row.id)}
                          aria-label={`Revoke ${row.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Connect snippet */}
      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-medium">Connect to the Colab MCP server</h3>
          <Button size="sm" variant="outline" onClick={() => copy(snippet, "Config")}>
            <Copy className="mr-1.5 h-3.5 w-3.5" />
            Copy config
          </Button>
        </div>
        <p className="mb-2 text-xs text-muted-foreground">
          Add this to your client&apos;s <code>.mcp.json</code> and replace{" "}
          <code>YOUR_TOKEN</code> with the token above.
        </p>
        <pre className="overflow-x-auto rounded-lg border bg-muted/30 p-3 font-mono text-xs">
          {snippet}
        </pre>
      </div>
    </section>
  );
}
